import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { 
  UserPlus, 
  Users, 
  DollarSign, 
  Shield, 
  TrendingUp, 
  Zap, 
  ArrowRight, 
  HelpCircle, 
  CheckCircle, 
  Gift,
  Star
} from 'lucide-react';
import './TransformYourSavings.css';

// Import mobile version only
import TransformYourSavingsMobile from './mobile/TransformYourSavings_mobile';

// Organic blob backgrounds for visual interest
const OrganicBlobs = () => (
  <div className="transform-savings-blobs">
    <div className="transform-blob blob-1"></div>
    <div className="transform-blob blob-2"></div>
    <div className="transform-blob blob-3"></div>
  </div>
);

// Enhanced background particles for depth
const EnhancedParticles = () => (
  <div className="transform-particles">
    {[...Array(20)].map((_, i) => (
      <div 
        key={i} 
        className={`transform-particle particle-${i % 5}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          animationDelay: `${Math.random() * 8}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Animated process step with interactive hover effects
const ProcessStep = ({ icon, title, description, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className="transform-savings-process-step"
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.div 
        className="transform-savings-process-icon"
        animate={{ 
          scale: isHovered ? 1.15 : 1,
          boxShadow: isHovered 
            ? "0 10px 25px rgba(255, 51, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.2)" 
            : "0 5px 15px rgba(0, 0, 0, 0.1)"
        }}
      >
        {icon}
        <div className="icon-pulse"></div>
      </motion.div>
      
      <motion.div 
        className="transform-savings-process-content"
        animate={{ x: isHovered ? 5 : 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <h4 className="transform-savings-step-title">{title}</h4>
        <p className="transform-savings-step-description">{description}</p>
      </motion.div>
    </motion.div>
  );
};

// Enhanced benefit card with glassmorphism and motion effects
const BenefitCard = ({ icon, title, description, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className="transform-savings-benefit-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5, delay: 0.2 + index * 0.15 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      animate={{ 
        y: isHovered ? -8 : 0,
        boxShadow: isHovered 
          ? "0 20px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15)" 
          : "0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)"
      }}
    >
      <div className="card-shine"></div>
      <div className="card-noise"></div>
      
      <motion.div 
        className="transform-savings-benefit-icon"
        animate={{ 
          scale: isHovered ? 1.1 : 1,
          boxShadow: isHovered 
            ? "0 10px 25px rgba(255, 51, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.2)" 
            : "0 5px 15px rgba(0, 0, 0, 0.1)"
        }}
      >
        {icon}
        <div className="icon-glow"></div>
      </motion.div>
      
      <h4 className="transform-savings-benefit-title">{title}</h4>
      <p className="transform-savings-benefit-description">{description}</p>
    </motion.div>
  );
};

// Enhanced FAQ preview with interactive animations
const FAQPreview = ({ delay = 0.3 }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className="transform-savings-benefit-card transform-savings-faq-preview"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5, delay }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      animate={{ 
        y: isHovered ? -8 : 0,
        boxShadow: isHovered 
          ? "0 20px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15)" 
          : "0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)"
      }}
    >
      <div className="card-shine"></div>
      <div className="card-noise"></div>
      
      <motion.div 
        className="transform-savings-benefit-icon"
        animate={{ 
          scale: isHovered ? 1.1 : 1,
          boxShadow: isHovered 
            ? "0 10px 25px rgba(255, 51, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.2)" 
            : "0 5px 15px rgba(0, 0, 0, 0.1)"
        }}
      >
        <HelpCircle />
        <div className="icon-glow"></div>
      </motion.div>
      
      <h4 className="transform-savings-benefit-title">Frequently Asked Questions</h4>
      <p className="transform-savings-benefit-description">
        Find answers to common questions about joining circles, payment details, and maximizing your savings.
      </p>
      
      <motion.button
        className="transform-savings-faq-link"
        animate={{ 
          x: isHovered ? 5 : 0 
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {/* Add navigation to full FAQ page */}}
      >
        View All FAQs
        <motion.span
          animate={{
            x: isHovered ? 5 : 0
          }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <ArrowRight size={16} className="arrow-icon" />
        </motion.span>
      </motion.button>
    </motion.div>
  );
};

// Enhanced CTA button with animated effects
const EnhancedCTAButton = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      className="transform-savings-cta-button"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.6 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ 
        scale: 1.05,
        boxShadow: "0 15px 30px rgba(255, 51, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1)"
      }}
      whileTap={{ scale: 0.98 }}
    >
      <span className="button-text">{children}</span>
      <motion.span 
        className="button-icon"
        animate={{
          x: isHovered ? 5 : 0
        }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <ArrowRight size={18} />
      </motion.span>
      <div className="button-glow"></div>
    </motion.button>
  );
};

// Main component with responsive handling - proper fix for hooks rules
const TransformYourSavings = () => {
  // State to track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);
  
  // All hooks must be called unconditionally at the top level
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  // Parallax effects based on scroll
  const titleY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const leftColumnY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const rightColumnY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const processSteps = [
    { 
      icon: <UserPlus size={20} />, 
      title: "1. Join or Create a Circle", 
      description: "Sign up in minutes and either join an existing circle with open spots or create your own custom savings group." 
    },
    { 
      icon: <Users size={20} />, 
      title: "2. Grow Your Circle", 
      description: "Invite friends or join with strangers – the more members in your circle, the greater the savings for everyone." 
    },
    { 
      icon: <DollarSign size={20} />, 
      title: "3. Unlock Premium Discounts", 
      description: "Instantly access carrier multi-line discounts that are typically only available to families." 
    },
    { 
      icon: <Shield size={20} />, 
      title: "4. Enjoy Worry-Free Service", 
      description: "Our secure payment system and dedicated support team ensure a smooth experience for all members." 
    }
  ];

  const benefits = [
    { 
      icon: <TrendingUp size={20} />, 
      title: "Save Up to $540 Annually", 
      description: "Most members save $40-$45 monthly compared to individual plans – that's hundreds of dollars back in your pocket every year." 
    },
    { 
      icon: <Zap size={20} />, 
      title: "Premium Features, Lower Prices", 
      description: "Get unlimited data, 5G access, and premium features typically reserved for expensive individual plans." 
    },
    { 
      icon: <Users size={20} />, 
      title: "Join Any Circle, Anytime", 
      description: "No contracts or commitments – easily switch between circles as your needs change, with no penalties or fees." 
    }
  ];
  
  // Use conditional rendering within the return statement instead of early return
  return (
    <>
      {isMobile ? (
        <TransformYourSavingsMobile />
      ) : (
        <section className="transform-savings-wrapper" ref={sectionRef}>
          {/* Visual enhancement elements - keeping original background elements */}
          <div className="noise-overlay"></div>
          <OrganicBlobs />
          <EnhancedParticles />
          <div className="gradient-overlay"></div>
          
          <div className="transform-savings-container">
            {/* Section header with animated title */}
            <motion.div 
              className="transform-savings-header"
              style={{ y: titleY }}
            >
              <div className="section-title-container">
                <motion.h2 
                  className="transform-savings-section-title"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                >
                  How <span className="gradient-text">Circledin</span> Transforms Your Savings
                </motion.h2>
                <div className="title-underline"></div>
              </div>
              
              <motion.h3 
                className="transform-savings-section-subtitle"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Stop overpaying for phone service. Join the group savings revolution today!
              </motion.h3>
            </motion.div>
            
            {/* Main content with two columns */}
            <div className="transform-savings-content">
              {/* Left column - Journey to Savings */}
              <motion.div 
                className="transform-savings-journey"
                style={{ y: leftColumnY }}
              >
                <motion.h3 
                  className="transform-savings-subsection-title"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                >
                  Your Journey to Savings
                </motion.h3>
                
                <div className="transform-savings-process-timeline">
                  {processSteps.map((step, index) => (
                    <ProcessStep key={index} {...step} index={index} />
                  ))}
                </div>
              </motion.div>
              
              {/* Right column - Benefits - UPDATED to fix overlapping issues */}
              <motion.div 
                className="transform-savings-benefits"
                style={{ y: rightColumnY }}
              >
                <motion.h3 
                  className="transform-savings-subsection-title"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                >
                  Why Circledin Works for You
                </motion.h3>
                
                <div className="transform-savings-benefits-grid">
                  {benefits.map((benefit, index) => (
                    <div className="benefit-card-wrapper" key={index}>
                      <BenefitCard {...benefit} index={index} />
                    </div>
                  ))}
                  <div className="benefit-card-wrapper">
                    <FAQPreview />
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* CTA button */}
            <div className="transform-savings-cta">
              <EnhancedCTAButton>
                Start Saving Today
              </EnhancedCTAButton>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default TransformYourSavings;